import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ProfileHero } from "@/components/runboom/profile-hero";
import { Wordmark } from "@/components/runboom/marks";
import { FlexCard } from "@/components/runboom/flex-card";
import { RaceCard } from "@/components/runboom/race-card";
import { runners, upcomingRaces } from "@/lib/mock";
import { formatRaceTime } from "@/lib/format";

export const Route = createFileRoute("/u/$handle/")({
  loader: ({ params }) => {
    const runner = runners[params.handle];
    if (!runner) throw notFound();
    return { runner };
  },
  component: ProfilePage,
});

function ProfilePage() {
  const { runner } = Route.useLoaderData();
  const rivals = Object.values(runners).filter((r) => r.handle !== runner.handle);

  const chasing = [...upcomingRaces]
    .map((race) => {
      const pb = runner.pbs.find((p) => p.distance === race.distanceLabel);
      const gap = pb ? race.yourPredictedSeconds - pb.timeSeconds : 0;
      return { race, gap };
    })
    .sort((a, b) => a.gap - b.gap)
    .slice(0, 2);

  return (
    <main className="mx-auto max-w-[1280px] px-5 py-6 sm:px-8 sm:py-10">
      <TopBar />

      <nav className="mt-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-muted">
        <Link to="/" className="hover:text-fg">RUNBOOM</Link>
        <span aria-hidden>/</span>
        <span className="text-fg">@{runner.handle}</span>
      </nav>

      <section className="mt-6 sm:mt-8">
        <ProfileHero runner={runner} />
      </section>

      <section className="mt-8 sm:mt-10">
        <FlexCard runner={runner} />
      </section>

      <section className="mt-8 sm:mt-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
          Currently chasing
        </div>
        <h2 className="mt-1 font-display text-[28px] leading-[28px] tracking-[-0.015em] text-fg sm:text-[44px] sm:leading-[44px]">
          NEXT PBs IN REACH
        </h2>
        <div className="mt-4 flex flex-col gap-3">
          {chasing.map(({ race, gap }) => (
            <div key={race.slug} className="relative">
              <Link
                to="/races/$slug"
                params={{ slug: race.slug }}
                className="block transition-transform hover:-translate-y-0.5"
              >
                <RaceCard race={race} />
              </Link>
              <div className="pointer-events-none absolute right-5 top-4 hidden sm:block">
                {gap < 0 ? (
                  <span
                    className="rounded-full px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-night"
                    style={{ backgroundColor: "var(--color-acid-lime)" }}
                  >
                    BOOM −{formatRaceTime(Math.abs(gap))}
                  </span>
                ) : gap === 0 ? (
                  <span className="rounded-full border border-fg-subtle/40 bg-bg-sunken px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
                    on PB
                  </span>
                ) : (
                  <span className="rounded-full border border-border bg-bg-sunken px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-muted">
                    +{formatRaceTime(gap)} off PB
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8 sm:mt-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
          Settle it
        </div>
        <h2 className="mt-1 font-display text-[28px] leading-[28px] tracking-[-0.015em] text-fg sm:text-[44px] sm:leading-[44px]">
          RACE A RIVAL
        </h2>
        <div className="mt-4 flex flex-wrap gap-3">
          {rivals.map((r) => (
            <Link
              key={r.handle}
              to="/u/$handle/vs/$rival"
              params={{ handle: runner.handle, rival: r.handle }}
              className="group flex items-center gap-3 rounded-full border border-border bg-bg-elevated px-4 py-2 transition-colors hover:border-blood-orange"
            >
              <img
                src={r.avatar}
                alt=""
                className="h-7 w-7 rounded-full object-cover"
              />
              <span className="font-mono text-[12px] uppercase tracking-[0.05em] text-fg">
                vs {r.displayName}
              </span>
              <span className="font-mono text-[13px] text-fg-muted group-hover:text-accent">→</span>
            </Link>
          ))}
        </div>
      </section>

      <footer className="mt-16 flex items-center justify-between border-t border-border pt-6 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-subtle sm:mt-20">
        <span>Every PB is a BOOM.</span>
        <span>Run to be remembered.</span>
      </footer>
    </main>
  );
}

function TopBar() {
  return (
    <div className="flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <Wordmark size="sm" />
      </Link>
      <nav className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted sm:gap-5">
        <Link to="/oracle" className="hidden hover:text-fg sm:inline">Oracle</Link>
        <Link to="/races" className="hidden hover:text-fg sm:inline">Races</Link>
        <Link
          to="/leaderboards"
          search={{ distance: "10K", city: "Cape Town" }}
          className="hidden hover:text-fg sm:inline"
        >
          Leaderboards
        </Link>
        <Link to="/wall" className="hidden hover:text-fg sm:inline">Wall</Link>
        <Link
          to="/me"
          className="rounded-full px-3 py-1.5 text-night transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: "var(--color-acid-lime)" }}
        >
          Connect Strava
        </Link>
      </nav>
    </div>
  );
}
